import {
    RecentEntitiesModel,
    RecentEntitiesModelAutocompleteEnriched,
} from 'core/services/recent-entities-service/recent-entities-model';
import { getReportEntityClosingDate } from 'core/services/report-entities-service/report-entities-service';
import { Badge, MultilineItemProps } from '@placer-ui/components';
import type { BasePlaceEntity } from '@placer-ui/types';
import {
    extractPoiRelevantProperties,
    getPoiIcon,
    getPoiWarningInfo,
} from 'hooks/use-entity-search/utils/extract-from-poi-utils';
import { isAddressType } from 'utils/poi-provider/poi-provider';
import React from 'react';
import { getFeatureFlags } from 'core/flow-control';
import { getPOIStatusTextIndicator } from 'utils/poi-status-text-indicator/poi-status-text-indicator';

type AutoCompleteSuggestion = {
    data: RecentEntitiesModelAutocompleteEnriched;
    term: string;
    hasUnlockNearbyActivity?: boolean;
    hasStoreIdBadgeAutocompleteSearchPermission?: boolean;
    enclosingComplexes?: BasePlaceEntity[];
};

export interface MultilineItemPropsWithEntity extends MultilineItemProps {
    entity: RecentEntitiesModel;
}

export const deprecatedLabelInfo = ({
    poi,
    hasUnlockNearbyActivity,
}: Omit<AutoCompleteSuggestion, 'data'> & { poi: BasePlaceEntity }) => {
    const closingDate = getReportEntityClosingDate(poi);

    const {
        isPrivacyFlag,
        isRestricted,
        isNearbyProfile,
        noUnlockNearbyActivity,
        flaggedTooltipText,
    } = extractPoiRelevantProperties({
        poi,
        hasUnlockNearbyActivity,
    });

    const hasAnyWarning =
        isPrivacyFlag || !hasUnlockNearbyActivity || isNearbyProfile || !!isRestricted;

    const warningInfo = getPoiWarningInfo({
        hasAnyWarning,
        isPrivacyFlag,
        isRestricted,
        isNearbyProfile,
        noUnlockNearbyActivity,
        flaggedTooltipText,
        closingDate,
    });

    if (closingDate) {
        return warningInfo?.tooltipText;
    }
    if (hasAnyWarning) {
        return flaggedTooltipText;
    }
    return undefined;
};

export const convertPoiToAutoCompleteSuggestion = (
    suggestionData: AutoCompleteSuggestion,
): MultilineItemPropsWithEntity => {
    const { data, hasUnlockNearbyActivity, enclosingComplexes } = suggestionData;
    const poi = data as BasePlaceEntity;
    const { enable_updated_flagged_poi_microcopy_ff } = getFeatureFlags();
    const { isPrivacyFlag, poiName, poiId, location } = extractPoiRelevantProperties({
        poi,
        hasUnlockNearbyActivity,
    });

    let finalName = poiName;

    // Locked poi functionality
    const { forceLockIconState, purchased } = data;
    let isLocked;
    if (forceLockIconState) {
        isLocked = forceLockIconState === 'show';
    } else {
        isLocked = !purchased;
    }

    if (isAddressType(poi)) {
        const exactName = poi.address?.formatted_address || poiName;
        finalName = exactName;
    }

    const Icon = getPoiIcon(poi);
    const badgeId = poi.store_id || poi.market_code;

    return {
        label: finalName,
        Badge: badgeId ? <Badge label={`#${badgeId}`} /> : undefined,
        description: location,
        value: poiId,
        Icon,
        isDisabled: isPrivacyFlag,
        entity: data,
        labelInfo: enable_updated_flagged_poi_microcopy_ff
            ? getPOIStatusTextIndicator(poi, enclosingComplexes)
            : deprecatedLabelInfo({ ...suggestionData, poi }),
        isLocked,
    };
};
